import React, { useEffect, useMemo, useRef, useState } from "react"
import styles from "./webhook.module.css"
import Button from "../../components/Form/Button"
import DatePicker from "../../components/Form/DatePicker"
import Select from "../../components/Form/Select"
import FormGroup from "../../components/Form/FormGroup"
import FormGroupInline from "../../components/Form/FormGroupInline"
import BreakForm from "../../components/Form/BreakForm"
import Table from "../../components/Table"
import Modal from "../../components/Modal"
import ModalDelete from "../../components/Modal/ModalDelete"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import ModalWebhook from "../../components/Modal/ModalWebhook"
import api from "../../services/api"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading"
import BackButton from "../../components/BackButton"
import { useParams } from "react-router-dom"
import { MdAdd, MdHistory, MdWebhook, MdSchedule, MdCheckCircle, MdContentCopy, MdDeleteOutline, MdDownload, MdErrorOutline, MdOutlineRefresh, MdPowerSettingsNew, MdReplay } from "react-icons/md"

const emptyFilters = { status: "", webhook_id: "", action: "", date_from: "", date_to: "" }
const deliveryFilterParams = filters => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value))
    if (filters.date_from) params.date_from = new Date(`${filters.date_from}T00:00:00`).toISOString()
    if (filters.date_to) {
        const end = new Date(`${filters.date_to}T00:00:00`)
        end.setDate(end.getDate() + 1)
        params.date_to = end.toISOString()
    }
    return params
}
const formatDate = value => value ? new Date(value).toLocaleString("pt-BR") : "—"
const formatJson = value => {
    if (value === undefined || value === null || value === "") return "Sem conteúdo"
    if (typeof value === "string") {
        try { return JSON.stringify(JSON.parse(value), null, 2) } catch { return value }
    }
    return JSON.stringify(value, null, 2)
}
const statusLabel = { success: "Entregue", failed: "Falhou", retrying: "Nova tentativa", pending: "Pendente", delivering: "Enviando", ignored: "Ignorado" }
const endpointHeaders = ["#", "Identificação", "URL", "Gatilhos", "Status", "Sucesso (30d)", "Última entrega", ""]
const deliveryHeaders = ["#", "Status", "Evento", "Endpoint", "HTTP", "Duração", "Data"]

const Webhook = () => {
    const [tab, setTab] = useState("endpoints")
    const [webhooks, setWebhooks] = useState([])
    const [actions, setActions] = useState([])
    const [filterOptions, setFilterOptions] = useState({ funnels: [], stages: [], teams: [], inboxes: [], pages: [], wabas: [] })
    const [deliveries, setDeliveries] = useState([])
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
    const [filters, setFilters] = useState(emptyFilters)
    const [draftFilters, setDraftFilters] = useState(emptyFilters)
    const [editor, setEditor] = useState(null)
    const [deleteId, setDeleteId] = useState("")
    const [statusChange, setStatusChange] = useState(null)
    const [deliveryDetail, setDeliveryDetail] = useState(null)
    const [deliveryDetailTab, setDeliveryDetailTab] = useState("payload")
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const loadingMoreRef = useRef(false)
    const deliveryQueryRef = useRef(0)
    const { account_id } = useParams()

    const loadWebhooks = async () => {
        const [webhookResponse, actionResponse] = await Promise.all([api.get("/webhooks"), api.get("/webhooks/actions")])
        setWebhooks(webhookResponse.data?.data || [])
        const catalog = actionResponse.data?.data || []
        setActions(catalog.map(item => typeof item === "string" ? { id: item, name: item, category: "Eventos", description: "" } : item))
    }

    const loadFilterOptions = async () => {
        const endpoints = [
            api.get("/funnels"), api.get("/teams/onlyCrm"), api.get("/chat/inboxes"),
            api.get("/integrations/meta/accounts/linked"), api.get("/integrations/messenger/accounts/linked"),
            api.get("/integrations/instagram/accounts/linked"), api.get("/integrations/whatsapp-api/accounts/linked"),
        ]
        const [funnelsResult, teamsResult, inboxesResult, metaResult, messengerResult, instagramResult, wabaResult] = await Promise.allSettled(endpoints)
        const data = result => result.status === "fulfilled" ? (result.value.data?.data || []) : []
        const funnelData = data(funnelsResult)
        const stageResults = await Promise.allSettled(funnelData.map(funnel => api.get(`/funnels/${funnel._id}/stages`)))
        const stages = stageResults.flatMap((result, index) => data(result).map(stage => ({ id: stage._id, value: `${funnelData[index].name} · ${stage.name}`, funnel_id: funnelData[index]._id })))
        const uniqueOptions = options => [...new Map(options.filter(item => item.id).map(item => [String(item.id), { ...item, id: String(item.id) }])).values()]
        const platformName = { whatsapp: "WhatsApp", whatsapp_api: "WhatsApp Cloud API", direct: "Instagram", messenger: "Messenger" }
        const inboxes = data(inboxesResult).map(inbox => ({ id: inbox._id, value: `${inbox.name} · ${platformName[inbox.platform] || inbox.platform || "Chat"}` }))
        const pages = [
            ...data(metaResult).map(page => ({ id: page.page_id, value: `${page.page_name || page.name || page.page_id} · Meta Ads` })),
            ...data(messengerResult).map(inbox => ({ id: inbox?.metadata?.page_id, value: `${inbox.name} · Messenger` })),
            ...data(instagramResult).map(inbox => ({ id: inbox?.metadata?.instagram_id || inbox?.metadata?.instagram_user_id, value: `${inbox.name} · Instagram` })),
        ]
        const wabas = data(wabaResult).map(inbox => ({ id: inbox?.metadata?.waba_id, value: `${inbox.name} · WhatsApp Business` }))
        setFilterOptions({
            funnels: funnelData.map(funnel => ({ id: funnel._id, value: funnel.name })),
            stages,
            teams: data(teamsResult).map(team => ({ id: team._id, value: team.name })),
            inboxes: uniqueOptions(inboxes), pages: uniqueOptions(pages), wabas: uniqueOptions(wabas),
        })
    }

    const loadDeliveries = async (page = 1, nextFilters = filters, append = false) => {
        const queryVersion = append ? deliveryQueryRef.current : ++deliveryQueryRef.current
        const params = { page, limit: 25, ...deliveryFilterParams(nextFilters) }
        const response = await api.get("/webhooks/deliveries", { params })
        if (queryVersion !== deliveryQueryRef.current) return
        const newDeliveries = response.data?.data || []
        setDeliveries(previous => append ? [...previous, ...newDeliveries.filter(item => !previous.some(current => current._id === item._id))] : newDeliveries)
        setPagination(response.data?.pagination || { page: 1, pages: 1, total: 0 })
    }

    const loadMoreDeliveries = async () => {
        if (loading || loadingMoreRef.current || pagination.page >= pagination.pages) return
        loadingMoreRef.current = true
        setLoadingMore(true)
        try {
            await loadDeliveries(pagination.page + 1, filters, true)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar mais entregas.")
        } finally {
            loadingMoreRef.current = false
            setLoadingMore(false)
        }
    }

    const refresh = async () => {
        setLoading(true)
        try {
            await Promise.all([loadWebhooks(), loadDeliveries(1), loadFilterOptions()])
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar os webhooks.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { refresh() }, [])

    const summary = useMemo(() => {
        const active = webhooks.filter(item => item.is_active).length
        const total = webhooks.reduce((sum, item) => sum + (item.metrics?.total || 0), 0)
        const successful = webhooks.reduce((sum, item) => sum + (item.metrics?.successful || 0), 0)
        const averageValues = webhooks.map(item => item.metrics?.average_response_time_ms).filter(value => Number.isFinite(value))
        return { active, total, successRate: total ? Math.round((successful / total) * 1000) / 10 : null, average: averageValues.length ? Math.round(averageValues.reduce((sum, value) => sum + value, 0) / averageValues.length) : null }
    }, [webhooks])

    const saveComplete = async () => {
        await loadWebhooks()
        await loadDeliveries(1)
    }

    const openWebhook = async (webhook, options = {}) => {
        if (!webhook?._id) return
        setLoading(true)
        try {
            const response = await api.get(`/webhooks/${webhook._id}`)
            setEditor({ isNew: false, value: response.data.data, ...options })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar o webhook.")
        } finally {
            setLoading(false)
        }
    }

    const removeWebhook = async id => {
        setLoading(true)
        try {
            const response = await api.delete(`/webhooks/${id}`)
            toast.success(response.data.message)
            setDeleteId("")
            await loadWebhooks()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível excluir.")
        } finally { setLoading(false) }
    }

    const changeWebhookStatus = async () => {
        if (!statusChange?.webhook?._id) return
        setLoading(true)
        try {
            const response = await api.patch(`/webhooks/${statusChange.webhook._id}/status`, { is_active: statusChange.isActive })
            toast.success(response.data.message)
            setStatusChange(null)
            await loadWebhooks()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível alterar o status do webhook.")
        } finally {
            setLoading(false)
        }
    }

    const testWebhook = async webhook => {
        setLoading(true)
        try {
            const response = await api.post(`/webhooks/${webhook._id}/test`, { action: webhook.actions[0] })
            toast.success(`${response.data.message} HTTP ${response.data?.data?.status_code || "-"} · ${response.data?.data?.response_time_ms || 0}ms`)
        } catch (err) {
            toast.error(err?.response?.data?.message || "O teste falhou.")
        } finally { setLoading(false); loadDeliveries(1) }
    }

    const openDelivery = async id => {
        setLoading(true)
        try {
            const response = await api.get(`/webhooks/deliveries/${id}`)
            setDeliveryDetail(response.data.data)
            setDeliveryDetailTab("payload")
        } catch (err) { toast.error(err?.response?.data?.message) }
        finally { setLoading(false) }
    }

    const retryDelivery = async id => {
        setLoading(true)
        try {
            const response = await api.post(`/webhooks/deliveries/${id}/retry`)
            toast[response.data?.data?.successful ? "success" : (response.data?.data?.queued_for_order || response.data?.data?.retry_scheduled ? "info" : "error")](response.data.message)
            const [detailResponse] = await Promise.all([api.get(`/webhooks/deliveries/${response.data.data._id}`), loadDeliveries(1)])
            setDeliveryDetail(detailResponse.data.data)
        } catch (err) { toast.error(err?.response?.data?.message || "Não foi possível reenviar.") }
        finally { setLoading(false) }
    }

    const replayFailedDeliveries = async () => {
        setLoading(true)
        try {
            const response = await api.post("/webhooks/deliveries/replay", { ...deliveryFilterParams(filters), status: "failed" })
            toast.success(response.data.message)
            await Promise.all([loadWebhooks(), loadDeliveries(1)])
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível reenviar as entregas.")
        } finally {
            setLoading(false)
        }
    }

    const exportDeliveries = async () => {
        try {
            const params = deliveryFilterParams(filters)
            const response = await api.get("/webhooks/deliveries/export", { params, responseType: "blob" })
            const url = URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = url
            link.download = `webhook-deliveries-${new Date().toISOString().slice(0, 10)}.csv`
            link.click()
            URL.revokeObjectURL(url)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível exportar as entregas.")
        }
    }

    const applyFilters = async next => {
        if (next.date_from && next.date_to && next.date_from > next.date_to) {
            toast.error("A data final deve ser igual ou posterior à data inicial.")
            return
        }
        setLoading(true)
        try {
            await loadDeliveries(1, next)
            setFilters(next)
            setDraftFilters(next)
        } catch (err) { toast.error(err?.response?.data?.message || "Não foi possível filtrar as entregas.") }
        finally { setLoading(false) }
    }
    const changeFilter = (key, value) => setDraftFilters(current => ({ ...current, [key]: value || "" }))

    const copyDeliveryValue = async value => {
        try {
            await navigator.clipboard.writeText(formatJson(value))
            toast.success("Conteúdo copiado.")
        } catch {
            toast.error("Não foi possível copiar o conteúdo.")
        }
    }

    const endpointRows = webhooks.map(webhook => ({
        id: webhook._id,
        value: [
            <div className={styles.nameCell}><strong>{webhook.name}</strong><span>{webhook.description || "Sem descrição"}</span></div>,
            <div className={styles.urlCell}><strong>{webhook.method || "POST"}</strong><span>{webhook.url}</span></div>,
            webhook.actions?.length || 0,
            <span className={`${styles.status} ${webhook.auto_paused ? styles.statusDanger : (webhook.is_active ? styles.statusSuccess : styles.statusNeutral)}`}>{webhook.auto_paused ? "Pausado por falhas" : (webhook.is_active ? "Ativo" : "Inativo")}</span>,
            webhook.metrics?.success_rate == null ? "—" : `${webhook.metrics.success_rate}%`,
            formatDate(webhook.last_delivery_at),
        ],
        actions: [
            { label: "Visualizar", onClick: () => openWebhook(webhook) },
            { label: "Editar", onClick: () => openWebhook(webhook, { initialEditable: true }) },
            { label: webhook.is_active && !webhook.auto_paused ? "Desativar webhook" : (webhook.auto_paused ? "Reativar webhook" : "Ativar webhook"), onClick: () => setStatusChange({ webhook, isActive: !(webhook.is_active && !webhook.auto_paused) }) },
            { label: "Ver entregas", onClick: () => { setTab("deliveries"); applyFilters({ ...emptyFilters, webhook_id: webhook._id }) } },
            { label: "Enviar teste", onClick: () => testWebhook(webhook) },
            { label: "Renovar segredo de assinatura", onClick: () => openWebhook(webhook, { initialSecretStep: "info" }) },
            { label: "Excluir", onClick: () => setDeleteId(webhook._id) },
        ],
    }))

    const deliveryRows = deliveries.map(delivery => ({
        id: delivery._id,
        value: [
            <span className={`${styles.status} ${delivery.successful ? styles.statusSuccess : (delivery.status === "failed" ? styles.statusDanger : styles.statusWarning)}`}>{statusLabel[delivery.status] || delivery.status}</span>,
            <div className={styles.eventCell}><strong>{actions.find(item => item.id === delivery.action)?.name || delivery.action}</strong><span>{delivery.action}</span>{delivery.is_test && <small>Teste</small>}{delivery.replay_of && <small className={styles.replayBadge}><MdReplay /> Reenvio</small>}</div>,
            delivery.webhook_id?.name || "Endpoint removido",
            delivery.status_code || "—",
            delivery.response_time_ms == null ? "—" : `${delivery.response_time_ms}ms`,
            formatDate(delivery.created_at),
        ],
    }))
    const canTryCurrentDelivery = ["pending", "retrying"].includes(deliveryDetail?.status)
    const currentDeliveryIsSending = deliveryDetail?.status === "delivering"
    const canCreateReplay = ["failed", "ignored"].includes(deliveryDetail?.status)
    const deliveryStatusDescription = deliveryDetail?.successful
        ? "O endpoint confirmou o recebimento deste evento."
        : (deliveryDetail?.status === "ignored"
            ? "Esta entrega não foi enviada porque o webhook estava inativo, pausado ou não estava mais disponível."
            : (deliveryDetail?.status === "failed"
                ? "O endpoint não confirmou o recebimento após todas as tentativas. Um novo reenvio criará outra entrega vinculada a esta."
                : (deliveryDetail?.status === "retrying"
                    ? "Uma nova tentativa está agendada nesta mesma entrega. Tentar agora apenas antecipa essa execução."
                    : (deliveryDetail?.status === "delivering" ? "A tentativa atual está sendo processada pelo endpoint." : "A entrega aguarda o primeiro envio."))))

    return (
        <>
            <div className={styles.container}>
                <div className="page-back-row">
                    <BackButton fallback={`/${account_id}/integrations`} />
                </div>
                <FormGroup>
                    <FormGroupInline>
                        <div className={styles.header}>
                            <BreakForm title="Webhooks" showAction={false} />
                            <span>Configure endpoints, acompanhe entregas e consulte respostas.</span>
                        </div>
                        <div className={styles.headerActions}><Button variant="primary" onClick={() => setEditor({ isNew: true, value: {} })}><MdAdd /> Novo webhook</Button></div>
                    </FormGroupInline>
                <div className={styles.metrics}>
                    <div><MdWebhook /><span>Webhooks ativos</span><strong>{summary.active}</strong></div>
                    <div><MdHistory /><span>Entregas (30 dias)</span><strong>{summary.total}</strong></div>
                    <div><MdCheckCircle /><span>Sucesso (30 dias)</span><strong>{summary.successRate === null ? "—" : `${summary.successRate}%`}</strong></div>
                    <div><MdSchedule /><span>Tempo médio (30 dias)</span><strong>{summary.average === null ? "—" : `${summary.average} ms`}</strong></div>
                </div>
                <div className={styles.actionsBar}>
                    <div className={styles.tabs}>
                        <Button variant={tab === "endpoints" ? "primary" : "secondary"} fullWidth={false} onClick={() => setTab("endpoints")}><MdWebhook /> Webhooks</Button>
                        <Button variant={tab === "deliveries" ? "primary" : "secondary"} fullWidth={false} onClick={() => setTab("deliveries")}><MdHistory /> Histórico</Button>
                    </div>
                    <Button variant="secondary" fullWidth={false} onClick={refresh}><MdOutlineRefresh /> Atualizar</Button>
                </div>

                {tab === "endpoints" ? (
                        <Table headers={endpointHeaders} values={endpointRows} messageNotRegisters="Nenhum webhook encontrado" onClick={id => openWebhook(webhooks.find(item => item._id === id))} showIndex={true} />
                ) : (
                    <>
                        <form className={styles.historyFilters} onSubmit={event => { event.preventDefault(); applyFilters(draftFilters) }}>
                            <div className={styles.historyFilterFields}>
                                <FormGroup labelText="Webhook"><Select name="Todos os webhooks" options={webhooks.map(item => ({ id: item._id, value: item.name }))} selected={draftFilters.webhook_id} onChange={value => changeFilter("webhook_id", value)} /></FormGroup>
                                <FormGroup labelText="Evento"><Select name="Todos os eventos" options={actions.map(item => ({ id: item.id, value: item.name }))} selected={draftFilters.action} onChange={value => changeFilter("action", value)} /></FormGroup>
                                <FormGroup labelText="Status"><Select name="Todos os status" options={Object.entries(statusLabel).map(([id, value]) => ({ id, value }))} selected={draftFilters.status} onChange={value => changeFilter("status", value)} /></FormGroup>
                                <FormGroup labelText="Período"><DatePicker name="Todo o período" multiple={true} dateStart={draftFilters.date_from} dateEnd={draftFilters.date_to} onChange={(start, end) => setDraftFilters(current => ({ ...current, date_from: start || "", date_to: end || "" }))} /></FormGroup>
                            </div>
                            <div className={styles.historyFilterFooter}>
                                <small>{deliveries.length} de {pagination.total} entregas carregadas · Período pela data de registro</small>
                                <div className={styles.historyFilterActions}>
                                    <Button type="button" variant="secondary" onClick={() => applyFilters(emptyFilters)}>Limpar filtros</Button>
                                    <Button type="submit" variant="primary">Aplicar filtros</Button>
                                </div>
                            </div>
                        </form>
                        <div className={styles.historyTools}>
                            <Button variant="secondary" fullWidth={false} onClick={replayFailedDeliveries}><MdReplay /> Reenviar falhas</Button>
                            <Button variant="secondary" fullWidth={false} onClick={exportDeliveries}><MdDownload /> Exportar CSV</Button>
                        </div>
                        <Table className={styles.historyTable} headers={deliveryHeaders} values={deliveryRows} count={pagination.total} messageNotRegisters="Nenhuma entrega encontrada" onClick={openDelivery} onScroll={loadMoreDeliveries} loadMore={loadingMore} showIndex={true} />
                    </>
                )}
                </FormGroup>
            </div>

            {editor && <ModalWebhook isNew={editor.isNew} value={editor.value} actions={actions} filterOptions={filterOptions} initialSecretStep={editor.initialSecretStep} initialEditable={editor.initialEditable} onSaved={saveComplete} closeModal={() => setEditor(null)} />}
            {deleteId && <ModalDelete closeModal={() => setDeleteId("")} onSubmit={removeWebhook} icon={<MdDeleteOutline />} title="Excluir endpoint" description="O endpoint deixará de receber eventos. O histórico de entregas será preservado." id={deleteId} />}
            {statusChange && <ModalConfirm
                icon={<MdPowerSettingsNew />}
                title={statusChange.isActive ? "Ativar webhook" : "Desativar webhook"}
                description={statusChange.webhook.name}
                text={statusChange.isActive
                    ? "Novos eventos voltarão a ser enviados. O contador de falhas consecutivas será zerado, mas entregas antigas não serão reenviadas automaticamente."
                    : "Novos eventos deixarão de ser enviados e entregas ainda pendentes poderão ser ignoradas. O histórico e as configurações serão preservados."}
                confirmLabel={statusChange.isActive ? "Ativar webhook" : "Desativar webhook"}
                confirmVariant={statusChange.isActive ? "primary" : "danger"}
                onSubmit={changeWebhookStatus}
                closeModal={() => setStatusChange(null)}
            />}
            {deliveryDetail && <Modal icon={deliveryDetail.successful ? <MdCheckCircle /> : (deliveryDetail.status === "failed" || deliveryDetail.status === "ignored" ? <MdErrorOutline /> : <MdOutlineRefresh />)} title="Detalhes da entrega" description={`${deliveryDetail.action} · ${deliveryDetail.event_id || deliveryDetail._id}`} closeModal={() => setDeliveryDetail(null)} fullWidith={true} heightFitContent={true} bodyClassName={styles.deliveryModalBody}>
                <div className={styles.deliveryContent}>
                    <section className={`${styles.deliveryHero} ${deliveryDetail.successful ? styles.deliveryHeroSuccess : (deliveryDetail.status === "failed" || deliveryDetail.status === "ignored" ? styles.deliveryHeroDanger : styles.deliveryHeroWarning)}`}>
                        <div className={styles.deliveryHeroMain}>
                            <span className={styles.deliveryStatusIcon}>{deliveryDetail.successful ? <MdCheckCircle /> : (deliveryDetail.status === "failed" || deliveryDetail.status === "ignored" ? <MdErrorOutline /> : <MdOutlineRefresh />)}</span>
                            <div>
                                <div className={styles.deliveryHeroTitle}><strong>{statusLabel[deliveryDetail.status] || deliveryDetail.status}</strong>{deliveryDetail.is_test && <span>Teste</span>}{deliveryDetail.replay_of && <span>Reenvio</span>}</div>
                                <p>{deliveryStatusDescription}</p>
                            </div>
                        </div>
                        {canTryCurrentDelivery && <Button fullWidth={false} onClick={() => retryDelivery(deliveryDetail._id)}><MdReplay /> Tentar agora</Button>}
                        {currentDeliveryIsSending && <Button fullWidth={false} disabled><MdOutlineRefresh /> Envio em andamento</Button>}
                        {canCreateReplay && <Button fullWidth={false} onClick={() => retryDelivery(deliveryDetail._id)}><MdReplay /> Criar novo reenvio</Button>}
                    </section>

                    <section className={styles.deliveryMetrics}>
                        <div><span>Webhook</span><strong>{deliveryDetail.webhook_id?.name || "Endpoint removido"}</strong></div>
                        <div><span>Status HTTP</span><strong>{deliveryDetail.status_code || "—"}</strong></div>
                        <div><span>Duração</span><strong>{deliveryDetail.response_time_ms == null ? "—" : `${deliveryDetail.response_time_ms} ms`}</strong></div>
                        <div><span>Tentativas</span><strong>{deliveryDetail.attempts || 0}</strong></div>
                        <div><span>Enviado em</span><strong>{formatDate(deliveryDetail.created_at)}</strong></div>
                    </section>

                    <section className={styles.deliveryEndpoint}>
                        <div><span>Destino</span><code>{deliveryDetail.url || "Endpoint não disponível"}</code></div>
                        <div><span>ID da entrega</span><code>{deliveryDetail.event_id || deliveryDetail._id}</code></div>
                    </section>

                    {deliveryDetail.replay_chain?.length > 1 && <section className={styles.deliveryRelation}>
                        <div className={styles.deliveryRelationHeader}>
                            <div><strong>Histórico de reenvios</strong><span>Cada reenvio possui um novo ID, mas permanece vinculado à entrega original.</span></div>
                            {deliveryDetail.webhook_id?.preserve_order && <small>Ordem preservada</small>}
                        </div>
                        <div className={styles.deliveryRelationTrack}>
                            {deliveryDetail.replay_chain.map((item, index) => {
                                const current = String(item._id) === String(deliveryDetail._id)
                                return <button type="button" key={item._id} className={current ? styles.current : ""} onClick={() => !current && openDelivery(item._id)} disabled={current}>
                                    <span className={`${styles.relationDot} ${item.successful ? styles.relationSuccess : (item.status === "failed" || item.status === "ignored" ? styles.relationDanger : styles.relationPending)}`}>{index === 0 ? "1" : index + 1}</span>
                                    <span><b>{index === 0 ? "Envio original" : `Reenvio ${index}`}</b><small>{statusLabel[item.status] || item.status} · {formatDate(item.created_at)}</small></span>
                                </button>
                            })}
                        </div>
                    </section>}

                    <section className={styles.deliveryInspector}>
                        <div className={styles.deliveryInspectorTabs}>
                            {[
                                ["payload", "Payload"], ["response", "Resposta"], ["headers", "Headers"],
                                ["attempts", `Tentativas (${deliveryDetail.attempt_history?.length || 0})`],
                            ].map(([id, label]) => <button type="button" key={id} className={deliveryDetailTab === id ? styles.active : ""} onClick={() => setDeliveryDetailTab(id)}>{label}</button>)}
                        </div>

                        {deliveryDetailTab === "payload" && <div className={styles.deliveryCodePanel}>
                            <div><strong>Payload enviado</strong><button type="button" onClick={() => copyDeliveryValue(deliveryDetail.payload)}><MdContentCopy /> Copiar</button></div>
                            <pre>{formatJson(deliveryDetail.payload)}</pre>
                        </div>}
                        {deliveryDetailTab === "response" && <div className={styles.deliveryCodePanel}>
                            <div><strong>Resposta recebida</strong><button type="button" onClick={() => copyDeliveryValue(deliveryDetail.response ?? deliveryDetail.error_message)}><MdContentCopy /> Copiar</button></div>
                            {deliveryDetail.error_message && <p className={styles.deliveryError}>{deliveryDetail.error_message}</p>}
                            <pre>{formatJson(deliveryDetail.response)}</pre>
                        </div>}
                        {deliveryDetailTab === "headers" && <div className={styles.deliveryHeadersGrid}>
                            <div className={styles.deliveryCodePanel}><div><strong>Headers enviados</strong><button type="button" onClick={() => copyDeliveryValue(deliveryDetail.request_headers)}><MdContentCopy /> Copiar</button></div><pre>{formatJson(deliveryDetail.request_headers)}</pre></div>
                            <div className={styles.deliveryCodePanel}><div><strong>Headers recebidos</strong><button type="button" onClick={() => copyDeliveryValue(deliveryDetail.response_headers)}><MdContentCopy /> Copiar</button></div><pre>{formatJson(deliveryDetail.response_headers)}</pre></div>
                        </div>}
                        {deliveryDetailTab === "attempts" && <div className={styles.deliveryTimeline}>
                            {(deliveryDetail.attempt_history || []).map((attempt, index) => <article key={`${attempt.attempted_at}-${index}`} className={attempt.successful ? styles.attemptSuccess : styles.attemptFailed}>
                                <span className={styles.attemptDot}></span>
                                <div className={styles.attemptHeader}><strong>Tentativa {index + 1}</strong><time>{formatDate(attempt.attempted_at)}</time></div>
                                <div className={styles.attemptMeta}><span>HTTP <b>{attempt.status_code || "—"}</b></span><span>Duração <b>{attempt.response_time_ms == null ? "—" : `${attempt.response_time_ms} ms`}</b></span><span>{attempt.successful ? "Confirmada" : "Sem confirmação"}</span></div>
                                {attempt.error_message && <p>{attempt.error_message}</p>}
                            </article>)}
                            {!deliveryDetail.attempt_history?.length && <div className={styles.deliveryEmptyState}>Nenhuma tentativa registrada até o momento.</div>}
                        </div>}
                    </section>

                    <footer className={styles.deliveryFooter}>
                        <span>{deliveryDetail.next_attempt_at ? `Próxima tentativa: ${formatDate(deliveryDetail.next_attempt_at)}` : `Última atualização: ${formatDate(deliveryDetail.updated_at || deliveryDetail.created_at)}`}</span>
                        <Button variant="secondary" fullWidth={false} onClick={() => setDeliveryDetail(null)}>Fechar</Button>
                    </footer>
                </div>
            </Modal>}
            {loading && <Loading fullPage={true} />}
        </>
    )
}

export default Webhook
